import { type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Card } from './Card';
import { Display } from './Display';
import { SmallCap } from './SmallCap';
import { useCanWriteOrg, useIsOrgAdmin } from './useMyOrgRole';

/**
 * Centralized copy for permission-related disabled-state explanations.
 *
 * Used as the `title=` attribute on disabled buttons across the app so
 * the message is identical everywhere. Native `title=` works fine on
 * disabled buttons in Chrome/Firefox and matches the existing ad-hoc
 * pattern (see `WholesalerQuickActions`, `OrderDetailPage`).
 *
 * These are module-level constants (no hook context), so prefer
 * `useReadOnlyTooltip()` / `useAdminOnlyTooltip()` in components; the
 * constants remain as untranslated fallbacks for non-component code.
 */
export const READ_ONLY_TOOLTIP =
  'Read-only — ask an admin to change your role.';

/** Tooltip for affordances available only to org admins (owner/admin). */
export const ADMIN_ONLY_TOOLTIP = 'Admin access required.';

/** Translated equivalent of {@link READ_ONLY_TOOLTIP} for use in components. */
export function useReadOnlyTooltip(): string {
  const { t } = useTranslation();
  return t('ui.permissions.readOnlyTooltip', {
    defaultValue: READ_ONLY_TOOLTIP,
  });
}

/** Translated equivalent of {@link ADMIN_ONLY_TOOLTIP} for use in components. */
export function useAdminOnlyTooltip(): string {
  const { t } = useTranslation();
  return t('ui.permissions.adminOnlyTooltip', {
    defaultValue: ADMIN_ONLY_TOOLTIP,
  });
}

/**
 * Renders the supplied form / editor `children` for writers
 * (owner / admin / operator). Viewers see a friendly "read-only" stub
 * instead, with an optional `backHref` link to bounce them back to the
 * relevant list page.
 *
 * Use on full-page create / edit screens (e.g. `ClientNewPage`,
 * `OrderNewPage`, `ProductNewPage`, `ContractNewPage`) so viewers who
 * land on those URLs via direct navigation see a clear message
 * instead of a form that would `42501` on submit.
 *
 * For inline buttons (Add row, Save, Delete) on list / detail pages
 * use the `useCanWriteOrg()` hook directly and hide the affordance.
 *
 * `backHref` uses a plain `<a>` so this component stays free of any
 * react-router-dom dependency — every SPA that imports `@pc/ui` is
 * fine with a full-page navigation back to the list URL.
 */
export function ReadOnlyGate({
  children,
  resourceLabel,
  backHref,
  backLabel,
}: {
  children: ReactNode;
  resourceLabel: string;
  backHref?: string;
  backLabel?: string;
}) {
  const { t } = useTranslation();
  const canWrite = useCanWriteOrg();
  if (canWrite) return <>{children}</>;
  return (
    <div className="max-w-2xl">
      <header className="mb-6">
        <SmallCap>{t('ui.permissions.readOnlyTitle')}</SmallCap>
        <Display size="md" as="h2" className="mt-1">
          {resourceLabel}
        </Display>
      </header>
      <Card>
        <p className="text-sm text-ink-2">
          <Trans
            i18nKey="ui.permissions.readOnlyBody"
            components={{ b: <strong /> }}
          />
        </p>
        {backHref ? (
          <p className="mt-4">
            <a
              href={backHref}
              className="text-sm text-ink underline decoration-rule-strong underline-offset-2 hover:text-ink-2"
            >
              {backLabel ?? t('ui.permissions.goBack')}
            </a>
          </p>
        ) : null}
      </Card>
    </div>
  );
}

/**
 * Admin-tier counterpart to `<ReadOnlyGate>`.
 *
 * Renders `children` only when the current user is an org admin
 * (owner / admin). Operators / viewers see an "admin access required"
 * card with the same visual treatment as the read-only gate. Use on
 * Settings pages whose body should only render for admins (Team,
 * Webhooks, API keys, Templates, Store, etc.), replacing the
 * duplicated `if (!isAdmin) return …` early-returns those pages
 * carry today.
 */
export function AdminOnlyGate({
  children,
  resourceLabel,
  backHref,
  backLabel,
}: {
  children?: ReactNode;
  resourceLabel: string;
  backHref?: string;
  backLabel?: string;
}) {
  const { t } = useTranslation();
  const isAdmin = useIsOrgAdmin();
  if (isAdmin) return <>{children}</>;
  return (
    <div className="max-w-2xl">
      <header className="mb-6">
        <SmallCap>{t('ui.permissions.adminOnlyTitle')}</SmallCap>
        <Display size="md" as="h2" className="mt-1">
          {resourceLabel}
        </Display>
      </header>
      <Card>
        <p className="text-sm text-ink-2">
          <Trans
            i18nKey="ui.permissions.adminOnlyBody"
            components={{ b: <strong /> }}
          />
        </p>
        {backHref ? (
          <p className="mt-4">
            <a
              href={backHref}
              className="text-sm text-ink underline decoration-rule-strong underline-offset-2 hover:text-ink-2"
            >
              {backLabel ?? t('ui.permissions.goBack')}
            </a>
          </p>
        ) : null}
      </Card>
    </div>
  );
}

/**
 * Persistent banner shown at the top of the operational app shell when
 * the current user is a viewer. Renders nothing for writers so the
 * shell stays unchanged for them.
 *
 * Mount once in the shell (e.g. `AppShell`, `OrgAppShell`) above the
 * routed content so viewers see one consistent message regardless of
 * which module they're currently inside.
 */
export function ReadOnlyBanner() {
  const canWrite = useCanWriteOrg();
  if (canWrite) return null;
  return (
    <div
      role="status"
      className="border-b border-rule bg-paper-2 px-4 py-2 text-xs text-ink-2"
    >
      <Trans
        i18nKey="ui.permissions.bannerBody"
        components={{ b: <strong className="font-medium text-ink" /> }}
      />
    </div>
  );
}
